import {
  ETIQUETA_ORIGEN_MOVIMIENTO,
  ETIQUETA_ORIGEN_MOVIMIENTO_EN,
  type Movimiento,
  type PaginaDeMovimientos,
} from '@cnc/contracts';
import {useEffect, useState, type ReactNode} from 'react';
import {Link, useSearchParams} from 'react-router';
import {todosLosMovimientos, useMovimientos} from '../api/cashflow';
import {et, tr} from '../i18n/idioma';
import {mes, usd} from '../utilidades/formato';
import {AYUDA_FILTROS_CASHFLOW as AF, AYUDA_MOVIMIENTOS} from './ayuda-cashflow';
import {DEFINICION_EN_PROCESO, DEFINICION_MOVIMIENTOS} from './columnas-cashflow';
import {NotaDeAyuda} from './nota-de-ayuda';
import {FechaSegun, REGLA_DESEMBOLSO, REGLA_PAGO_AL_FINANCIADOR} from './fecha-segun';
import {Paginacion} from './paginacion';
import {SelectorMultiple} from './selector-multiple';
import {TablaDeCashflowComun} from './tabla-de-cashflow';

// Los filtros de esta vista (en la direccion, con ?vista=movimientos).
const CLAVES = [
  'empresa',
  'origen',
  'estado',
  'linea',
  'desde',
  'hasta',
  'anioEtd',
  'mesEtd',
  'buscar',
  'pagina',
  'porPagina',
  'orden',
  'direccion',
] as const;

// Tabla de movimientos (tambien la de "Vencido y próximos 30 días" del Resumen).
export function TablaDeMovimientos({
  filas,
  vacia,
  orden,
  altoMaximo,
  titulo,
  hoja,
  todasLasFilas,
}: {
  filas: Movimiento[];
  vacia: string;
  hoja?: string;
  todasLasFilas?: () => Promise<Movimiento[]>;
  orden?: {orden: string; direccion: 'asc' | 'desc'; onOrdenar: (orden: string) => void};
  altoMaximo?: string;
  titulo?: ReactNode;
}) {
  return (
    <TablaDeCashflowComun
      tabla="movimientos"
      definicion={DEFINICION_MOVIMIENTOS}
      filas={filas}
      claveDeFila={x => x.clave}
      mensajeVacio={vacia}
      orden={orden}
      altoMaximo={altoMaximo}
      titulo={titulo}
      hoja={hoja}
      todasLasFilas={todasLasFilas}
      claseDeFila={x => (x.estado === 'realizado' ? 'text-apagado' : undefined)}
      celda={(columna, x) => {
        if (columna.id === 'exportacion')
          return (
            <Link to={`/seguimiento/${encodeURIComponent(x.exportacion)}`} className="text-verde hover:underline">
              {x.exportacion}
            </Link>
          );
        if (columna.id === 'fecha') return <FechaSegun valor={x.fecha} segun={x.fechaSegun} />;
        if (columna.id === 'estado' && x.estado === 'vencido')
          return <span className="font-medium text-alerta">⚠ {columna.valor(x)}</span>;
        if (columna.id === 'concepto')
          return (
            <span title={et(ETIQUETA_ORIGEN_MOVIMIENTO, ETIQUETA_ORIGEN_MOVIMIENTO_EN)[x.origen]}>{x.concepto}</span>
          );
        return undefined;
      }}
    />
  );
}

// Lo que se esta financiando ahora (Daniel, 8/10/2026): financiaciones
// abiertas, primero las ya desembolsadas (el dinero que esta afuera).
function FinanciacionEnCurso({datos}: {datos: PaginaDeMovimientos['enProceso']}) {
  const [abierto, setAbierto] = useState(true);
  return (
    <section
      aria-label={tr('En proceso: lo que se está financiando', 'In process: what is being financed')}
      className="rounded-lg border border-linea bg-white p-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setAbierto(a => !a)}
          aria-expanded={abierto}
          className="text-sm font-semibold text-verde-oscuro"
        >
          {abierto ? '▾' : '▸'} {tr('En proceso: lo que se está financiando', 'In process: what is being financed')}
        </button>
        <span className="rounded-md bg-dorado-claro px-2 py-0.5 text-sm" data-prueba="total-en-proceso">
          {tr('Financiado ahora', 'Financed now')}: <b className="tabular-nums">{usd(datos.financiadoUsd)}</b> ·{' '}
          {tr('Por financiar', 'To be financed')}: <b className="tabular-nums">{usd(datos.porFinanciarUsd)}</b> ·{' '}
          {datos.elementos.length === 1
            ? tr('1 exportación', '1 export')
            : tr(`${datos.elementos.length} exportaciones`, `${datos.elementos.length} exports`)}
        </span>
        <NotaDeAyuda texto={AF.totalEnProceso} />
        {datos.porLinea.map(l => (
          <span
            key={l.linea}
            className="rounded-md border border-linea px-2 py-0.5 text-sm"
            title={tr(
              `${l.financiador}: financiado ahora (ocupa el cupo) y por financiar`,
              `${l.financiador}: financed now (uses the line) and to be financed`,
            )}
          >
            {l.linea}: <b className="tabular-nums">{usd(l.financiadoUsd)}</b>
            {l.porFinanciarUsd > 0 && <span className="text-apagado"> + {usd(l.porFinanciarUsd)}</span>}{' '}
            <span className="text-xs text-apagado">({l.cantidad})</span>
          </span>
        ))}
      </div>
      {abierto && (
        <div className="mt-2">
          <TablaDeCashflowComun
            tabla="enProceso"
            hoja={tr('En proceso', 'In process')}
            definicion={DEFINICION_EN_PROCESO}
            filas={datos.elementos}
            claveDeFila={x => `${x.exportacion}-${x.linea}`}
            altoMaximo="20rem"
            mensajeVacio={tr(
              'No hay financiaciones abiertas con estos filtros.',
              'No open financings with these filters.',
            )}
            claseDeFila={x => (x.ocupaCupo ? undefined : 'text-apagado')}
            celda={(columna, x) => {
              if (columna.id === 'exportacion')
                return (
                  <Link
                    to={`/seguimiento/${encodeURIComponent(x.exportacion)}#financiacion`}
                    className="text-verde hover:underline"
                  >
                    {x.exportacion}
                  </Link>
                );
              if (columna.id === 'desembolso')
                return (
                  <>
                    <FechaSegun valor={x.desembolso} segun={x.desembolsoSegun} regla={REGLA_DESEMBOLSO()} />
                    {x.desembolso && !x.desembolsado && (
                      <span className="ml-1 text-xs text-apagado">{tr('(esperado)', '(expected)')}</span>
                    )}
                  </>
                );
              if (columna.id === 'pagoComprador')
                return (
                  <span className={x.vencido ? 'font-medium text-alerta' : ''}>
                    {x.vencido && '⚠ '}
                    <FechaSegun
                      valor={x.pagoComprador}
                      segun={x.pagoCompradorSegun}
                      regla={REGLA_PAGO_AL_FINANCIADOR()}
                    />
                  </span>
                );
              return undefined;
            }}
          />
        </div>
      )}
    </section>
  );
}

// Filtros de año y mes de zarpe de la exportacion (Daniel, 13/10/2026), los
// mismos en el Resumen y en Movimientos.
export function FiltrosDePeriodo({
  parametros,
  opciones,
  cambiar,
}: {
  parametros: URLSearchParams;
  opciones:
    | {
        anios: Array<{valor: string; etiqueta: string; cantidad: number}>;
        meses: Array<{valor: string; etiqueta: string; cantidad: number}>;
      }
    | undefined;
  cambiar: (cambios: {anioEtd?: string; mesEtd?: string}) => void;
}) {
  return (
    <>
      <SelectorMultiple
        etiqueta={tr('Año de zarpe', 'Departure year')}
        ayuda={AF.anioEtd}
        valor={parametros.get('anioEtd') ?? undefined}
        opciones={opciones?.anios ?? []}
        onCambiar={v => cambiar({anioEtd: v})}
      />
      <SelectorMultiple
        etiqueta={tr('Mes de zarpe (ETD)', 'Departure month (ETD)')}
        ayuda={AF.mesEtd}
        valor={parametros.get('mesEtd') ?? undefined}
        opciones={(opciones?.meses ?? []).map(o => ({...o, etiqueta: o.valor === 'sin' ? o.etiqueta : mes(o.valor)}))}
        onCambiar={v => cambiar({mesEtd: v})}
      />
    </>
  );
}

// Cashflow → Movimientos: cada pago, desembolso y cobro con quien paga y quien
// recibe. Con una empresa elegida, el total de lo que le entra y le sale.
export function MovimientosDeCashflow() {
  const [parametros, setParametros] = useSearchParams();
  const consulta = new URLSearchParams();
  for (const clave of CLAVES) {
    const v = parametros.get(clave);
    if (v) consulta.set(clave, v);
  }
  const datos = useMovimientos(consulta.toString());
  const cambiar = (cambios: Partial<Record<(typeof CLAVES)[number], string | undefined>>) => {
    const nuevos = new URLSearchParams(window.location.search);
    for (const [k, v] of Object.entries(cambios)) {
      if (v) nuevos.set(k, v);
      else nuevos.delete(k);
    }
    if (!('pagina' in cambios)) nuevos.delete('pagina');
    setParametros(nuevos, {replace: true});
  };
  const [busqueda, setBusqueda] = useState(parametros.get('buscar') ?? '');
  useEffect(() => {
    const temporizador = setTimeout(() => {
      if ((parametros.get('buscar') ?? '') !== busqueda.trim()) cambiar({buscar: busqueda.trim() || undefined});
    }, 350);
    return () => clearTimeout(temporizador);
  });
  const d = datos.data;
  const conEmpresa = !!parametros.get('empresa');
  const campoFecha = 'mt-0.5 w-full rounded-md border border-linea px-2 py-1 text-sm text-tinta';
  const orden = parametros.get('orden') ?? '';
  const direccion = parametros.get('direccion') === 'desc' ? 'desc' : 'asc';
  return (
    <div className="space-y-3" data-prueba="cashflow-movimientos">
      <div className="grid items-end gap-2 rounded-lg border border-linea bg-white px-3 py-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-10">
        <label className="block text-xs text-apagado sm:col-span-2">
          {tr('Buscar', 'Search')}
          <NotaDeAyuda texto={AF.buscarMovimientos} dentroDeEtiqueta />
          <input
            type="search"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            placeholder={tr('Exportación, empresa o tipo de movimiento', 'Export, company or movement type')}
            className="mt-0.5 w-full rounded-md border border-linea px-3 py-1 text-sm text-tinta"
          />
        </label>
        <SelectorMultiple
          etiqueta={tr('Empresa', 'Company')}
          ayuda={AF.empresa}
          valor={parametros.get('empresa') ?? undefined}
          opciones={d?.opciones.empresas ?? []}
          onCambiar={v => cambiar({empresa: v})}
        />
        <SelectorMultiple
          etiqueta={tr('Tipo de movimiento', 'Movement type')}
          ayuda={AYUDA_MOVIMIENTOS.origen}
          valor={parametros.get('origen') ?? undefined}
          opciones={d?.opciones.origenes ?? []}
          onCambiar={v => cambiar({origen: v})}
        />
        <SelectorMultiple
          etiqueta={tr('Estado', 'Status')}
          ayuda={AYUDA_MOVIMIENTOS.estado}
          valor={parametros.get('estado') ?? undefined}
          opciones={d?.opciones.estados ?? []}
          onCambiar={v => cambiar({estado: v})}
        />
        <SelectorMultiple
          etiqueta={tr('Línea', 'Line')}
          ayuda={AF.lineaMovimientos}
          valor={parametros.get('linea') ?? undefined}
          opciones={d?.opciones.lineas ?? []}
          onCambiar={v => cambiar({linea: v})}
        />
        <FiltrosDePeriodo parametros={parametros} opciones={d?.opciones} cambiar={cambiar} />
        <label className="block text-xs text-apagado">
          {tr('Desde', 'From')}
          <NotaDeAyuda texto={AF.desde} dentroDeEtiqueta />
          <input
            type="date"
            value={parametros.get('desde') ?? ''}
            onChange={e => cambiar({desde: e.target.value || undefined})}
            className={campoFecha}
          />
        </label>
        <label className="block text-xs text-apagado">
          {tr('Hasta', 'To')}
          <NotaDeAyuda texto={AF.hasta} dentroDeEtiqueta />
          <input
            type="date"
            value={parametros.get('hasta') ?? ''}
            onChange={e => cambiar({hasta: e.target.value || undefined})}
            className={campoFecha}
          />
        </label>
      </div>
      {datos.isError && <p className="text-sm text-alerta">{datos.error.message}</p>}
      {d && (
        <div className="flex flex-wrap gap-1.5 text-xs" data-prueba="totales-movimientos">
          {conEmpresa ? (
            <>
              <span className="rounded-md border border-linea bg-white px-2 py-0.5">
                {tr('Entra', 'In')}:{' '}
                <b className="text-sm tabular-nums text-verde-oscuro">{usd(d.totales.entradasUsd)}</b>
              </span>
              <span className="rounded-md border border-linea bg-white px-2 py-0.5">
                {tr('Sale', 'Out')}: <b className="text-sm tabular-nums text-tinta">{usd(d.totales.salidasUsd)}</b>
              </span>
              <span className="rounded-md border border-linea bg-white px-2 py-0.5">
                {tr('Neto', 'Net')}:{' '}
                <b className="text-sm tabular-nums">
                  {d.totales.entradasUsd - d.totales.salidasUsd < 0 ? '−' : '+'}
                  {usd(Math.abs(d.totales.entradasUsd - d.totales.salidasUsd))}
                </b>
              </span>
              <NotaDeAyuda texto={AF.totalesEmpresa} />
            </>
          ) : (
            <span className="rounded-md border border-linea bg-white px-2 py-0.5">
              {tr('Total', 'Total')}: <b className="text-sm tabular-nums">{usd(d.totales.totalUsd)}</b>
              <span className="ml-2 text-apagado">
                {tr('(elige una empresa para ver lo que entra y sale)', '(choose a company to see money in and out)')}
              </span>
              <NotaDeAyuda titulo={tr('Total', 'Total')} texto={AF.total} />
            </span>
          )}
        </div>
      )}
      {d && <FinanciacionEnCurso datos={d.enProceso} />}
      <div className={datos.isFetching ? 'opacity-70' : ''}>
        <TablaDeMovimientos
          filas={d?.elementos ?? []}
          hoja={tr('Movimientos', 'Movements')}
          todasLasFilas={() => todosLosMovimientos(consulta.toString())}
          altoMaximo="calc(100vh - 22rem)"
          orden={{
            orden,
            direccion,
            onOrdenar: o =>
              cambiar({orden: o, direccion: orden === o && direccion === 'asc' ? 'desc' : 'asc', pagina: undefined}),
          }}
          vacia={
            datos.isPending
              ? tr('Calculando…', 'Calculating…')
              : tr('No hay movimientos con estos filtros.', 'No movements with these filters.')
          }
        />
      </div>
      {d && (
        <Paginacion
          pagina={d.pagina}
          totalPaginas={d.totalPaginas}
          porPagina={d.porPagina}
          total={d.total}
          onPagina={p => cambiar({pagina: String(p)})}
          onPorPagina={n => cambiar({porPagina: String(n)})}
          unidad={{es: 'movimientos', en: 'movements'}}
        />
      )}
    </div>
  );
}
